/**
 * Simple Video Block — Vimeo modal; mobile can use separate square poster and video_id_mobile.
 */

import { isBlockOnPage } from "../../src/js/modules/block-present.js";

(function() {
    'use strict';

    if (!isBlockOnPage("simple-video")) {
        return;
    }

    const playerMap = new WeakMap();
    let escapeHandler = null;
    // Match $breakpoint-lg (992px): phones in landscape are often >767px wide.
    const USE_MOBILE_VIMEO_AND_POSTER_MQ = '(max-width: 991px)';

    function vimeoEmbedUrl(videoId, privacyH, options) {
        const portrait = options && options.portrait;
        const params = new URLSearchParams({
            autoplay: '1',
            muted: '0',
            loop: '0',
            controls: '1',
            byline: '0',
            title: '0',
            portrait: portrait ? '1' : '0',
            dnt: '1',
            playsinline: '1',
        });
        if (privacyH) {
            params.set('h', privacyH);
        }
        return (
            'https://player.vimeo.com/video/' +
            encodeURIComponent(videoId) +
            '?' +
            params.toString()
        );
    }

    function useMobileVimeoLayout() {
        return window.matchMedia(USE_MOBILE_VIMEO_AND_POSTER_MQ).matches;
    }

    function getActiveVimeoConfig(meta, block) {
        const mobile = useMobileVimeoLayout();
        const idDesk = (
            meta.dataset.vimeoId ||
            meta.getAttribute('data-vimeo-id') ||
            ''
        ).trim();
        const idMobile = (
            meta.dataset.vimeoIdMobile ||
            meta.getAttribute('data-vimeo-id-mobile') ||
            ''
        ).trim();
        const hDesk = (
            meta.dataset.vimeoH || meta.getAttribute('data-vimeo-h') || ''
        ).trim();
        const hMobile = (
            meta.dataset.vimeoHMobile ||
            meta.getAttribute('data-vimeo-h-mobile') ||
            ''
        ).trim();

        let videoId = '';
        let privacyH = '';

        if (mobile && idMobile) {
            videoId = idMobile;
            privacyH = hMobile;
        } else if (mobile) {
            videoId = idDesk;
            privacyH = hDesk;
        } else if (idDesk) {
            videoId = idDesk;
            privacyH = hDesk;
        } else if (idMobile) {
            videoId = idMobile;
            privacyH = hMobile;
        }

        const usePortrait =
            mobile &&
            (block.classList.contains('simple-video--has-mobile-poster') ||
                idMobile !== '');

        return { videoId, privacyH, portrait: usePortrait };
    }

    function iframeConfigFingerprint(src) {
        if (!src) {
            return '';
        }
        try {
            const u = new URL(src, 'https://player.vimeo.com/');
            const m = u.pathname.match(/\/video\/(\d+)/);
            const id = m ? m[1] : '';
            const h = u.searchParams.get('h') || '';
            const portrait = u.searchParams.get('portrait') || '0';
            return id + '|' + h + '|' + portrait;
        } catch (e) {
            return src;
        }
    }

    function loadVimeoSDK() {
        return new Promise((resolve) => {
            if (window.Vimeo && window.Vimeo.Player) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://player.vimeo.com/api/player.js';
            script.async = true;
            script.onload = () => resolve();
            document.head.appendChild(script);
        });
    }

    function initSimpleVideo() {
        const blocks = document.querySelectorAll('.simple-video');

        blocks.forEach((block) => {
            if (block.dataset.simpleVideoInit) return;
            block.dataset.simpleVideoInit = '1';

            initScaleAnimation(block);

            if (block.classList.contains('simple-video--inline-video')) {
                return;
            }

            const meta = block.querySelector('.video-container');
            const modal = block.querySelector('.simple-video-modal');
            const backdrop = block.querySelector('.simple-video-modal__backdrop');
            const shell = block.querySelector('.simple-video-modal__shell');
            const closeBtn = block.querySelector('.simple-video-modal__close');
            const vimeoEmbed = modal ? modal.querySelector('.vimeo-embed') : null;
            const playBtn = block.querySelector('.play-button');

            if (!meta || !modal || !backdrop || !shell || !closeBtn || !vimeoEmbed || !playBtn) {
                return;
            }

            const idDesk = (meta.dataset.vimeoId || '').trim();
            const idMobile = (meta.dataset.vimeoIdMobile || '').trim();
            if (!idDesk && !idMobile) return;

            document.body.appendChild(modal);

            const inner = block.querySelector('.simple-video__media > .inner');

            function cleanupDuplicateIframes() {
                const frame = modal.querySelector('.simple-video-modal__frame');
                if (!frame) return;
                frame.querySelectorAll(':scope > iframe').forEach((iframe) => {
                    if (!vimeoEmbed.contains(iframe)) {
                        iframe.remove();
                    }
                });
            }

            cleanupDuplicateIframes();
            const observer = new MutationObserver(cleanupDuplicateIframes);
            const frameEl = modal.querySelector('.simple-video-modal__frame');
            if (frameEl) {
                observer.observe(frameEl, { childList: true, subtree: false });
            }

            function getOrCreatePlayer() {
                let player = playerMap.get(meta);
                if (player) return player;
                const iframe = vimeoEmbed.querySelector('iframe');
                if (!iframe || !window.Vimeo || !window.Vimeo.Player) return null;
                player = new window.Vimeo.Player(iframe);
                playerMap.set(meta, player);
                return player;
            }

            function startPlaybackFromPlayer(player) {
                if (!player) return;
                player
                    .ready()
                    .then(() => player.play())
                    .catch(() => {});
            }

            function syncModalLayout(cfg) {
                if (cfg.portrait) {
                    modal.classList.add('simple-video-modal--portrait');
                } else {
                    modal.classList.remove('simple-video-modal--portrait');
                }
            }

            function openModal(cfg) {
                syncModalLayout(cfg);
                block.classList.add('simple-video--modal-open');
                modal.classList.add('is-open');
                modal.setAttribute('aria-hidden', 'false');
                document.documentElement.classList.add('simple-video-modal-open');
                closeBtn.focus();
            }

            function closeModal() {
                const player = getOrCreatePlayer();
                if (player) {
                    player.pause().catch(() => {});
                }
                block.classList.remove('simple-video--modal-open');
                modal.classList.remove('is-open');
                modal.classList.remove('simple-video-modal--portrait');
                modal.setAttribute('aria-hidden', 'true');
                document.documentElement.classList.remove('simple-video-modal-open');
            }

            function removeVimeoIframe() {
                const iframe = vimeoEmbed.querySelector('iframe');
                if (iframe) {
                    iframe.remove();
                }
                playerMap.delete(meta);
            }

            function bindEscapeOnce() {
                if (escapeHandler) return;
                escapeHandler = (e) => {
                    if (e.key !== 'Escape') return;
                    const openModalEl = document.querySelector('.simple-video-modal.is-open');
                    if (!openModalEl) return;
                    const btn = openModalEl.querySelector('.simple-video-modal__close');
                    if (btn) btn.click();
                };
                document.addEventListener('keydown', escapeHandler);
            }

            bindEscapeOnce();

            const playVideo = (e) => {
                e.preventDefault();
                e.stopPropagation();

                const cfg = getActiveVimeoConfig(meta, block);
                if (!cfg.videoId) return;

                const targetSrc = vimeoEmbedUrl(cfg.videoId, cfg.privacyH, {
                    portrait: cfg.portrait,
                });

                const existing = vimeoEmbed.querySelector('iframe');
                if (existing) {
                    const cur = iframeConfigFingerprint(existing.src);
                    const next = iframeConfigFingerprint(targetSrc);
                    if (cur !== next) {
                        removeVimeoIframe();
                    }
                }

                if (!modal.classList.contains('is-open')) {
                    openModal(cfg);
                } else {
                    syncModalLayout(cfg);
                }

                if (vimeoEmbed.querySelector('iframe')) {
                    startPlaybackFromPlayer(getOrCreatePlayer());
                    return;
                }

                loadVimeoSDK().then(() => {
                    cleanupDuplicateIframes();
                    if (vimeoEmbed.querySelector('iframe')) {
                        startPlaybackFromPlayer(getOrCreatePlayer());
                        return;
                    }

                    const iframeEl = document.createElement('iframe');
                    iframeEl.src = targetSrc;
                    iframeEl.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
                    iframeEl.setAttribute('allowfullscreen', '');
                    vimeoEmbed.appendChild(iframeEl);

                    const player = new window.Vimeo.Player(iframeEl);
                    playerMap.set(meta, player);
                    startPlaybackFromPlayer(player);
                });
            };

            if (inner) {
                inner.style.cursor = 'pointer';
                inner.addEventListener('click', playVideo);
            }

            playBtn.addEventListener('click', playVideo);

            backdrop.addEventListener('click', (e) => {
                e.preventDefault();
                closeModal();
            });

            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeModal();
            });
        });
    }

    /**
     * Scale media from 90% → 100% (centred) on scroll.
     * Matches header-full-screen / large-image scale reveal.
     */
    function initScaleAnimation(block) {
        if (!block.classList.contains('simple-video--scale-animation')) {
            return;
        }

        const media = block.querySelector('.simple-video__media');
        if (!media) return;

        if (
            typeof window.gsap === 'undefined' ||
            typeof window.ScrollTrigger === 'undefined'
        ) {
            return;
        }

        const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;

        if (prefersReducedMotion) {
            window.gsap.set(media, { scale: 1, transformOrigin: '50% 50%' });
            return;
        }

        window.gsap.set(media, { transformOrigin: '50% 50%' });

        window.gsap.fromTo(
            media,
            { scale: 0.9 },
            {
                scale: 1,
                ease: 'expoScale(0.5,7,none)',
                scrollTrigger: {
                    trigger: block,
                    start: '-65% top',
                    end: '+=40%',
                    scrub: 0.2,
                    invalidateOnRefresh: true,
                    id: 'simple-video-scale-' + (block.id || 'default'),
                },
            },
        );
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initSimpleVideo);
    } else {
        initSimpleVideo();
    }
})();
